import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CSpinner,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
} from "@coreui/react";
import { TradingViewAdvanced } from "@src/components/TradingView/TVAdvanced";
import { processRunResult } from "@src/components/TradingView/utils";
import { preparePropsForDatafeed } from "@src/components/TradingView/utils/tvAdvanced";
import { useSymbols } from "@src/hooks/redux";
import { useCacheUrl } from "@src/hooks/useCacheUrl";
import { useLiveNow } from "@src/hooks/useLiveNow";
import { useValueWhileMounted } from "@src/hooks/useValueWhileMounted";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import { calculateDuration } from "@src/utils/formatters";
import {
  downloadAndProcessParquet,
  downloadExecutionPreview,
  joinPreview,
} from "@src/utils/parquetUtils";
import { getAccountingCoverageMetrics } from "@src/utils/positionStatus";

import { TradeMonteCarloPanel } from "./MonteCarlo/TradeMonteCarloPanel";
import { RobustnessTab } from "./Robustness/RobustnessTab";
import { RunPerformance } from "./RunPerformance";
import { RunPositions } from "./RunPositions";
import { RunResultProvider, useRunResultContext } from "./RunResultContext";

/** Shows indicator when user changed resolution from original (using API candles) */
const ResolutionChangedIndicator: React.FC = () => {
  const { candleSource } = useRunResultContext();

  if (!candleSource.isResolutionChanged) return null;

  return (
    <CAlert color="info" className="mb-2 py-2">
      <small>
        <strong>Custom resolution:</strong> Showing {candleSource.currentResolution} candles from
        API (original: {candleSource.originalResolution}). Indicators were calculated for the
        original resolution and may not align accurately.
      </small>
    </CAlert>
  );
};

interface Props {
  run: T.ApiRun;
  type: "backtest" | "execution";
  virtualizedTable?: boolean;
  editableIndicators?: boolean;
  executionBoundaries?: T.ExecutionBoundary[];
}

export const mapRunPositions = (
  positions: NonNullable<T.ApiRun["positions"]>,
  executionBoundaries?: T.ExecutionBoundary[],
): T.AppPosition[] => {
  // In group mode, sort by (execution_id, start_ts) to keep each execution's
  // positions together
  const sortedPositions = [...positions].sort((a, b) => {
    if (executionBoundaries?.length) {
      const execDiff = (a.execution_id ?? 0) - (b.execution_id ?? 0);
      if (execDiff !== 0) return execDiff;
    }
    return new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime();
  });
  return sortedPositions.map((position) => ({
    ...position,
    stop_loss: null,
    take_profit: null,
    tv_shape_id: null,
  })) as T.AppPosition[];
};

export const RunBlock: React.FC<Props> = ({
  run,
  type,
  virtualizedTable = false,
  editableIndicators,
  executionBoundaries,
}) => {
  // The cache on screen, kept with the URL it came from so a newer cache can replace it
  const [loadedCache, setLoadedCache] = useState<{
    url: string;
    data: T.ProcessedCacheData;
  } | null>(null);
  const [symbol, setSymbolState] = useState<T.TDT.ApiSymbol | null>(null);
  const [positions, setPositions] = useState<T.AppPosition[] | null>(null);
  // The cache URL whose download failed, so it stops counting as loading
  const [failedCacheUrl, setFailedCacheUrl] = useState<string | null>(null);
  // A running execution's newest candles and indicators, drawn over the end of the cache
  const [preview, setPreview] = useState<{ runId: number; data: T.ProcessedCacheData } | null>(
    null,
  );
  const { nowUtc, refresh: refreshNow } = useLiveNow();
  const { getSymbol, setSymbol } = useSymbols();

  // The download in flight, so a repeated effect run for the same URL reuses it instead of fetching again
  const downloadRef = useRef<{ url: string; promise: Promise<T.ProcessedCacheData> } | null>(null);
  const symbolLoadedRef = useRef(false);
  // The run the page shows now, so a superseded download started for an earlier run does not land on this one
  const currentRunIdRef = useValueWhileMounted(run.id);

  // Get cache URL (handles job polling for running executions)
  const {
    cacheUrl,
    loading: cacheLoading,
    refreshing: cacheRefreshing,
    refreshError: cacheRefreshError,
    error: cacheError,
    canAllowIncompleteCandles,
    retryAllowingIncompleteCandles,
  } = useCacheUrl(run, type);
  const { confirmModal } = useConfirmModal();

  const handleAllowIncompleteCandles = async () => {
    const { confirmed } = await confirmModal(
      "Publish the cache despite the candle gap?\n\nThe metrics will be computed on an incomplete frame, so returns, drawdown, and the rest may be silently off. Only do this once you have confirmed the missing candles are a genuine exchange gap, not a fixable ingestion problem.",
      { title: "Allow Incomplete Candles" },
    );
    if (confirmed) retryAllowingIncompleteCandles();
  };

  // Reset positions and the cache on screen when run changes to avoid showing stale data
  useEffect(() => {
    setPositions(null);
    setLoadedCache(null);
  }, [run.id]);

  // Reset symbol loading when run changes to avoid stale symbol data
  useEffect(() => {
    symbolLoadedRef.current = false;
    setSymbolState(null);
  }, [run.id]);

  // Load positions immediately from run.positions (independent of cache)
  useEffect(() => {
    if (run.positions && run.positions.length > 0) {
      const appPositions = mapRunPositions(run.positions, executionBoundaries);
      setPositions(appPositions);
    } else {
      setPositions([]);
    }
  }, [run.positions, executionBoundaries]);

  // Load symbol independently (needed for positions display even without cache)
  useEffect(() => {
    if (symbolLoadedRef.current) return;

    const symbolName = run.config?.symbol_name;
    const exchangeName = run.config?.exchange_name;

    if (!symbolName || !exchangeName) return;

    // Check if symbol is already cached in Redux
    const cachedSymbol = getSymbol(exchangeName, symbolName);
    if (cachedSymbol) {
      setSymbolState(cachedSymbol);
      symbolLoadedRef.current = true;
      return;
    }

    // Fetch symbol from API
    const loadSymbol = async () => {
      try {
        const symbolData = await tdApi.getSymbol(exchangeName, symbolName);
        if (symbolData) {
          setSymbol(exchangeName, symbolName, symbolData);
          setSymbolState(symbolData);
          symbolLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load symbol:", err);
      }
    };
    loadSymbol();
  }, [run.config?.symbol_name, run.config?.exchange_name, getSymbol, setSymbol]);

  // A running execution's worker publishes its newest candles after each one closes. Fetched alongside
  // the cache, so the newest candles show without waiting for a fresh cache to be generated.
  // A group page's synthetic run carries the group's id, which names no execution's preview
  const isGroupRun = "_is_group_synthetic" in run && !!run._is_group_synthetic;
  const isRunningExecution = type === "execution" && !run.end_ts && !isGroupRun;
  useEffect(() => {
    if (!isRunningExecution) return;
    let cancelled = false;
    downloadExecutionPreview(run.id)
      .then((data) => {
        if (!cancelled && data) setPreview({ runId: run.id, data });
      })
      .catch((err) => console.warn("Chart preview unavailable:", err));
    return () => {
      cancelled = true;
    };
  }, [run.id, isRunningExecution]);

  // Load cache data from remote parquet file (optional - for chart and performance). When a fresh cache
  // replaces the stored one, the stored one stays on screen until the fresh one has been downloaded.
  useEffect(() => {
    if (!cacheUrl) {
      return;
    }
    // Prevent double-fetching the same URL
    if (loadedCache?.url === cacheUrl) {
      return;
    }

    if (downloadRef.current?.url !== cacheUrl) {
      downloadRef.current = { url: cacheUrl, promise: downloadAndProcessParquet(cacheUrl) };
    }
    const { promise } = downloadRef.current;
    const runId = currentRunIdRef.current;
    let superseded = false;

    const loadAsync = async () => {
      // Timeout after 30s (e.g., Cloudflare blocked...)
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error("Cache download timeout")), 30000);
        });
        const rawData = await Promise.race([promise, timeoutPromise]);
        // A superseded download still fills an empty chart of the same run until the newer one lands
        if (!superseded) setLoadedCache({ url: cacheUrl, data: rawData });
        else if (currentRunIdRef.current === runId) {
          setLoadedCache((current) => current ?? { url: cacheUrl, data: rawData });
        }
      } catch (err) {
        // Don't block rendering - chart falls back to API mode when cacheData is null
        console.error("Failed to load cache:", err);
        if (downloadRef.current?.promise === promise) downloadRef.current = null;
        if (!superseded) setFailedCacheUrl(cacheUrl);
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }
    };
    loadAsync();

    return () => {
      superseded = true;
    };
  }, [cacheUrl, loadedCache?.url, currentRunIdRef]);

  const previewData = preview?.runId === run.id ? preview.data : null;
  // The preview is drawn only on top of a downloaded cache: on its own it lacks the cache's money curve
  // and older candles
  const cacheData = useMemo(
    () => (loadedCache ? joinPreview(loadedCache.data, previewData) : null),
    [loadedCache, previewData],
  );
  const showsPreview = !!previewData && cacheData !== (loadedCache?.data ?? null);
  const parquetLoading = !!cacheUrl && loadedCache?.url !== cacheUrl && failedCacheUrl !== cacheUrl;
  // Generating the fresh cache failed, or it was generated but could not be downloaded
  const refreshProblem =
    cacheRefreshError ??
    (cacheUrl !== null && failedCacheUrl === cacheUrl && loadedCache?.url !== cacheUrl
      ? "the new chart data failed to download"
      : null);

  // Combined loading state: loading cache URL or loading parquet
  const isLoadingChart = cacheLoading || parquetLoading;

  // The results below are memoized on the values they are built from

  // Build runResult if we have ALL data including cache (needed for chart)
  const rawRunResult = useMemo(
    () =>
      symbol && positions && cacheData ? processRunResult(run, symbol, positions, cacheData) : null,
    [run, symbol, positions, cacheData],
  );
  const runResult = useMemo(
    () => (rawRunResult ? { ...rawRunResult, executionBoundaries } : null),
    [rawRunResult, executionBoundaries],
  );

  // Build a minimal run result for positions-only view (when cache is not available)
  const positionsOnlyResult = useMemo<T.RunResult | null>(
    () =>
      !runResult && positions && symbol
        ? {
            api_period: run.config?.timeframe || "5m",
            candles: [],
            indicators: [],
            // Executions default 50 (safety), backtests default 1000 elsewhere (interpretability)
            initial_capital: run.config?.portfolio?.initial_capital ?? 50,
            metrics: run.metrics || ({} as T.TDT.BacktestMetrics),
            positions,
            series: [],
            symbol,
            start_ts: run.start_ts || undefined,
            end_ts: run.end_ts || undefined,
            executionBoundaries,
          }
        : null,
    [runResult, positions, symbol, run, executionBoundaries],
  );

  // Use full runResult if available, otherwise use positions-only
  const displayResult = runResult || positionsOnlyResult;
  const hasFullData = !!runResult;

  // The run's stored cache is on screen while a newer one is still on its way
  const isShowingStoredCache = hasFullData && (cacheRefreshing || parquetLoading);
  const storedCacheTs = "cache_ts" in run ? run.cache_ts : undefined;
  const storedCacheDuration =
    storedCacheTs && loadedCache?.url === run.cache_url
      ? calculateDuration(storedCacheTs, nowUtc.toISOString())
      : null;
  const storedCacheAge = storedCacheDuration === "0m" ? "under a minute" : storedCacheDuration;
  // The page's clock is otherwise re-read about once an hour, so re-read it every minute while an age shows
  const showsStoredCacheAge = !!storedCacheDuration && (isShowingStoredCache || !!refreshProblem);
  useEffect(() => {
    if (!showsStoredCacheAge) return;
    refreshNow();
    const timer = window.setInterval(refreshNow, 60_000);
    return () => window.clearInterval(timer);
  }, [showsStoredCacheAge, storedCacheTs, refreshNow]);

  // The chart's props. The options object is built inside the memo, which depends on editableIndicators itself
  const datafeedProps = useMemo(
    () =>
      displayResult
        ? preparePropsForDatafeed(displayResult, !hasFullData, { editableIndicators })
        : null,
    [displayResult, hasFullData, editableIndicators],
  );

  // Show loading only if we have nothing to show yet
  if (!displayResult || !datafeedProps) {
    return (
      <CAlert color="info" className="d-flex align-items-center">
        <CSpinner size="sm" className="me-2" />
        Loading results...
      </CAlert>
    );
  }

  return (
    <div className="mt-4">
      {!displayResult ? (
        <div className="text-center p-4">
          <CSpinner title="Loading results..." />
          <p className="mt-2 mb-0">Loading results...</p>
        </div>
      ) : (
        <RunResultProvider key={run.id} run={run} runType={type}>
          {/* Show loading indicator while chart data is being prepared */}
          {isLoadingChart && !hasFullData && (
            <CAlert color="info" className="mb-2">
              <CSpinner size="sm" className="me-2" />
              <strong>Loading chart data:</strong>{" "}
              {cacheLoading ? "Generating cache..." : "Processing data..."}
            </CAlert>
          )}

          {/* The stored cache is on screen while a fresh one is generated or downloaded */}
          {isShowingStoredCache && (
            <CAlert color="info" className="CacheRefreshing mb-2 py-2">
              <CSpinner size="sm" className="me-2" />
              <small>
                <strong>Updating:</strong>{" "}
                {showsPreview
                  ? `showing the latest candles. Trade levels and the money curve${storedCacheAge ? ` are from ${storedCacheAge} ago and` : ""} update when the full data is ready.`
                  : `showing the stored chart data${storedCacheAge ? ` from ${storedCacheAge} ago` : ""} while the latest is generated.`}
              </small>
            </CAlert>
          )}

          {/* The refresh failed, so the stored cache stays on screen */}
          {refreshProblem && hasFullData && (
            <CAlert color="warning" className="CacheRefreshFailed mb-2 py-2">
              <small>
                <strong>Could not update the chart data:</strong>{" "}
                {showsPreview
                  ? `showing the latest candles. Trade levels and the money curve are from the stored cache${storedCacheAge ? ` from ${storedCacheAge} ago` : ""}.`
                  : `showing the stored data${storedCacheAge ? ` from ${storedCacheAge} ago` : ""}.`}{" "}
                ({refreshProblem})
              </small>
            </CAlert>
          )}

          {/* Show API fallback mode indicator when cache fails but chart still renders */}
          {!hasFullData && !isLoadingChart && (
            <CAlert color="warning" className="mb-2">
              <strong>Live chart mode:</strong> Displaying real-time candles from API.
              {cacheError && ` (${cacheError})`} Indicators and backtested data not available.
            </CAlert>
          )}

          {/* Operator override for a candle-gap cache failure: publish anyway on the incomplete frame */}
          {canAllowIncompleteCandles && !isLoadingChart && (
            <CAlert
              color="warning"
              className="CacheIncompleteOverride mb-2 d-flex align-items-center justify-content-between gap-2"
            >
              <span>
                <strong>Cache blocked by a candle gap:</strong> fresh metrics could not be generated
                because the candle data is incomplete. Publish anyway to compute metrics on the
                incomplete frame.
              </span>
              <CButton
                className="CacheIncompleteOverride-allow"
                color="warning"
                size="sm"
                onClick={handleAllowIncompleteCandles}
              >
                Allow incomplete candles
              </CButton>
            </CAlert>
          )}

          {/* Show indicator when user changed resolution (API candles) */}
          <ResolutionChangedIndicator />

          {/* TradingView chart - show with cache data OR in API fallback mode */}
          {(hasFullData || !isLoadingChart) && (
            <CCard className="tvAdvancedCard mb-2">
              <CCardBody className="p-0">
                <div style={{ height: "620px", width: "100%" }}>
                  {/* A new cache remounts the widget, which builds its chart once from the props */}
                  <TradingViewAdvanced
                    key={`${loadedCache?.url ?? "api"}${showsPreview ? "+preview" : ""}`}
                    {...datafeedProps}
                  />
                </div>
              </CCardBody>
            </CCard>
          )}

          <hr className="my-3" />

          {/* Tabs for Positions and Performance */}
          <CCard className="metricsCard">
            <CCardBody className="p-0">
              <CTabs defaultActiveItemKey="positions">
                <CTabList variant="underline-border" className="px-2 mb-1">
                  <CTab itemKey="positions">Positions</CTab>
                  <CTab itemKey="performance" disabled={!hasFullData}>
                    Performance {!hasFullData && "(requires cache)"}
                  </CTab>
                  {/* Backtest only: the endpoint resamples a closed-trade list, which a live
                      execution does not have until it ends. */}
                  {type === "backtest" && <CTab itemKey="monteCarlo">Monte Carlo</CTab>}
                  {/* Backtest only: robustness re-runs the strategy against a stored backtest as its
                      baseline; a live execution has no such baseline. */}
                  {type === "backtest" && <CTab itemKey="robustness">Robustness</CTab>}
                </CTabList>

                <CTabContent className="p-2">
                  <CTabPanel className="positionsTab" itemKey="positions">
                    <RunPositions
                      run={run}
                      runResult={displayResult}
                      type={type}
                      virtualizedTable={virtualizedTable}
                    />
                  </CTabPanel>
                  <CTabPanel className="performanceTab" itemKey="performance">
                    {hasFullData ? (
                      <RunPerformance
                        runResult={displayResult}
                        coverageMetrics={getAccountingCoverageMetrics(run)}
                      />
                    ) : (
                      <CAlert color="info">Performance metrics require cache data</CAlert>
                    )}
                  </CTabPanel>
                  {type === "backtest" && (
                    <CTabPanel className="monteCarloTab" itemKey="monteCarlo">
                      <TradeMonteCarloPanel backtestId={run.id} />
                    </CTabPanel>
                  )}
                  {type === "backtest" && (
                    <CTabPanel className="robustnessTab" itemKey="robustness">
                      <RobustnessTab baselineBacktestId={run.id} strategyId={run.strategy_id} />
                    </CTabPanel>
                  )}
                </CTabContent>
              </CTabs>
            </CCardBody>
          </CCard>
        </RunResultProvider>
      )}
    </div>
  );
};
